import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Search, Star, X, Mail, Phone } from "lucide-react";

const TIER_CLS = {
  platinum: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
  gold: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  silver: "bg-white/5 text-white/60 border-white/15",
  new: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
};

export default function Guests() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("");
  const [selected, setSelected] = useState(null);

  async function load() {
    const params = {};
    if (search) params.search = search;
    if (tier) params.loyalty = tier;
    const { data } = await api.get("/guests", { params });
    setData(data);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search, tier]);

  const tiers = ["", "platinum", "gold", "silver", "new"];
  if (!data) return <div className="text-white/50">Loading guests…</div>;

  return (
    <div className="space-y-8" data-testid="guests-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Guest CRM</div>
        <h1 className="font-display text-3xl md:text-4xl">Guest database</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat label="Total guests" value={data.summary.total} />
        <Stat label="Lifetime revenue" value={`₹${(data.summary.total_spent / 100000).toFixed(2)}L`} />
        <Stat label="Platinum" value={data.summary.by_tier?.platinum || 0} />
        <Stat label="Gold" value={data.summary.by_tier?.gold || 0} />
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 bg-[#12182B] border border-white/10 rounded-md px-3 py-2 flex-1 max-w-sm">
          <Search className="w-4 h-4 text-white/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone…" className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="guest-search" />
        </div>
        <div className="flex gap-2">
          {tiers.map((t) => (
            <button key={t || "all"} onClick={() => setTier(t)} className={`text-xs uppercase tracking-widest px-3 py-2 rounded-md border transition ${tier === t ? "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30" : "bg-transparent text-white/60 border-white/10 hover:text-white"}`} data-testid={`tier-${t || "all"}`}>
              {t || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="guests-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                <th className="px-5 py-4">Guest</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Bookings</th>
                <th className="px-5 py-4">Lifetime value</th>
                <th className="px-5 py-4">Tier</th>
                <th className="px-5 py-4">Last stay</th>
              </tr>
            </thead>
            <tbody>
              {data.guests.map((g) => (
                <tr key={g.id} onClick={() => setSelected(g.key)} className="border-b border-white/5 last:border-none hover:bg-[#1A2235] cursor-pointer" data-testid={`guest-row-${g.key}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium">{g.name}</div>
                    <div className="text-xs text-white/40">{g.channels.slice(0, 2).join(" · ")}</div>
                  </td>
                  <td className="px-5 py-4 text-white/70 text-xs">
                    {g.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{g.email}</div>}
                    {g.phone && <div className="flex items-center gap-1.5 mt-1"><Phone className="w-3 h-3" />{g.phone}</div>}
                  </td>
                  <td className="px-5 py-4 font-display text-base">{g.bookings}</td>
                  <td className="px-5 py-4 font-display text-base">₹{(g.total_spent).toLocaleString("en-IN")}</td>
                  <td className="px-5 py-4">
                    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${TIER_CLS[g.loyalty_tier] || TIER_CLS.new}`}>
                      {g.loyalty_tier}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-white/60 text-xs">{g.last_stay ? new Date(g.last_stay).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                </tr>
              ))}
              {data.guests.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-white/50">No guests match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <GuestDrawer guestKey={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#12182B] border border-white/5 rounded-xl p-5">
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-display text-3xl mt-3">{value}</div>
    </div>
  );
}

function GuestDrawer({ guestKey, onClose }) {
  const [g, setG] = useState(null);
  const [notes, setNotes] = useState("");
  const [tier, setTier] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/guests/${encodeURIComponent(guestKey)}`);
      setG(data); setNotes(data.notes || ""); setTier(data.loyalty_tier || "new");
    })();
  }, [guestKey]);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/guests/${encodeURIComponent(guestKey)}`, { notes, loyalty_tier: tier });
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  if (!g) return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-end">
      <div className="w-full max-w-lg bg-[#0a0f1c] h-full p-8 text-white/50">Loading…</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose} data-testid="guest-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0f1c] h-full overflow-y-auto border-l border-white/10">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37]">Guest profile</div>
            <div className="font-display text-2xl mt-1">{g.name}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Stays" value={g.bookings} />
            <Stat label="Spent" value={`₹${Math.round(g.total_spent / 1000)}k`} />
            <div className="bg-[#12182B] border border-white/5 rounded-xl p-5">
              <div className="text-xs uppercase tracking-widest text-white/50">Tier</div>
              <select value={tier} onChange={(e) => setTier(e.target.value)} className="mt-3 w-full bg-[#0a0f1c] border border-white/10 rounded-md px-2 py-1.5 text-sm" data-testid="tier-select">
                {["platinum", "gold", "silver", "new"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Contact</div>
            <div className="text-sm text-white/80 space-y-1">
              {g.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-white/40" />{g.email}</div>}
              {g.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-white/40" />{g.phone}</div>}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Private notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Preferences, allergies, special requests…" className="w-full bg-[#12182B] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60 resize-none" data-testid="guest-notes" />
            <button onClick={save} disabled={saving} className="btn-gold rounded-md px-4 py-2 text-sm mt-3 disabled:opacity-60" data-testid="save-guest">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Stay history ({g.reservations.length})</div>
            <div className="space-y-3">
              {g.reservations.map((r) => (
                <div key={r.id} className="bg-[#12182B] border border-white/5 rounded-md p-3 text-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">Room {r.room_number} · {r.room_type}</div>
                      <div className="text-xs text-white/50 mt-1">
                        {new Date(r.check_in).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} → {new Date(r.check_out).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · {r.nights}N
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-base">₹{Math.round(r.total_amount).toLocaleString("en-IN")}</div>
                      <div className="text-[10px] text-white/50 uppercase mt-1">{r.channel} · {r.status?.replace("_", " ")}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
